import { styled, Typography } from "@schaeffler-commerce/design-system";
import { RouterLink } from "~/common/components/Link";
import type ComponentTypes from "~/features/cms/componentTypes";
import classNames from "classnames";
import { isSiteRelativeUrl, resolveUrl } from "~/utils/url/urlResolver";

export interface CMSSimpleBannerMediaDTO {
  code?: string;
  url?: string;
  altText?: string;
}

export interface CMSSimpleBannerProps {
  type: ComponentTypes.CMSSimpleBanner;
  id: string;
  className?: string;
  urlLink?: string;
  media?: CMSSimpleBannerMediaDTO;
  headline?: string;
  content?: string;
}

export const CMSSimpleBanner = ({ urlLink, media, headline, content, className }: CMSSimpleBannerProps) => {
  const imageUrl = media?.url;

  if (!imageUrl) {
    return null;
  }

  const altText = media?.altText ?? headline ?? "";

  const bannerContent = (
    <>
      <BannerImage src={resolveUrl(imageUrl)} alt={altText} loading="lazy" />
      {(headline || content) && (
        <Overlay>
          {headline && (
            <Typography variant="headlineMedium" component="p">
              {headline}
            </Typography>
          )}
          {content && <Typography variant="bodyMedium">{content}</Typography>}
        </Overlay>
      )}
    </>
  );

  if (!urlLink) {
    return <Root className={classNames("scha__cms", className)}>{bannerContent}</Root>;
  }

  // Site-relative CMS urls navigate through the router; everything else stays a raw anchor.
  return isSiteRelativeUrl(urlLink) ? (
    <BannerLink to={urlLink} className={classNames("scha__cms", className)}>
      {bannerContent}
    </BannerLink>
  ) : (
    <BannerLink as="a" href={resolveUrl(urlLink)} className={classNames("scha__cms", className)}>
      {bannerContent}
    </BannerLink>
  );
};

const Root = styled("div")({ display: "block", width: "100%" });

const BannerImage = styled("img")({
  display: "block",
  width: "100%",
  height: "auto",
  maxWidth: "100%",
});

const BannerLink = styled(RouterLink)({ display: "block", width: "100%" });

const Overlay = styled("div")(({ theme }) => ({
  marginTop: theme.spacing(1),
}));
